import type {AnchorSide} from '@primer/behaviors'
import {createContext, type PropsWithChildren, use, useMemo} from 'react'

export interface OverlayDirectionContextProps {
  /**
   * The preferred side for overlay menus to open.
   * Defaults to 'outside-bottom' if not provided.
   */
  preferredOverlaySide: AnchorSide
}

const OverlayDirectionContext = createContext<OverlayDirectionContextProps | undefined>(undefined)

export const OverlayDirectionProvider = ({
  preferredOverlaySide,
  children,
}: PropsWithChildren<OverlayDirectionContextProps>) => {
  const providerData = useMemo(
    () => ({preferredOverlaySide}) satisfies OverlayDirectionContextProps,
    [preferredOverlaySide],
  )
  return <OverlayDirectionContext value={providerData}>{children}</OverlayDirectionContext>
}

/**
 * Returns the preferred overlay side from context, or 'outside-bottom' if no context is provided.
 */
export const useOverlayDirection = (): AnchorSide => {
  const context = use(OverlayDirectionContext)
  return context?.preferredOverlaySide ?? 'outside-bottom'
}

try{ OverlayDirectionContext.displayName ||= 'OverlayDirectionContext' } catch {}
try{ OverlayDirectionProvider.displayName ||= 'OverlayDirectionProvider' } catch {}